/**
 * AvailabilityEditor — mentor-facing controls for weekly rules,
 * one-off windows, and blackouts.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  useMentorAvailability,
  useMentorAvailabilityMutations,
} from "@/lib/mentors/calendar/useMentorCalendar";
import type { MentorBlackoutKind } from "@/lib/mentors/calendar/types";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const minutesToHHMM = (m: number) => {
  const h = String(Math.floor(m / 60)).padStart(2, "0");
  const min = String(m % 60).padStart(2, "0");
  return `${h}:${min}`;
};
const hhmmToMinutes = (s: string) => {
  const [h, m] = s.split(":").map(Number);
  return h * 60 + m;
};
const toLocalInputValue = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate(),
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const AvailabilityEditor = ({ mentorUserId }: { mentorUserId: string }) => {
  const { rules, windows, blackouts } = useMentorAvailability(mentorUserId);
  const m = useMentorAvailabilityMutations(mentorUserId);
  const { toast } = useToast();

  // Recurring rule form
  const [weekday, setWeekday] = useState("1");
  const [start, setStart] = useState("17:00");
  const [end, setEnd] = useState("20:00");
  const [slotMin, setSlotMin] = useState("30");

  // Window form
  const [winStart, setWinStart] = useState(toLocalInputValue(new Date()));
  const [winEnd, setWinEnd] = useState(
    toLocalInputValue(new Date(Date.now() + 60 * 60 * 1000)),
  );
  const [winLabel, setWinLabel] = useState("");

  // Blackout form
  const [blkStart, setBlkStart] = useState(toLocalInputValue(new Date()));
  const [blkEnd, setBlkEnd] = useState(
    toLocalInputValue(new Date(Date.now() + 24 * 60 * 60 * 1000)),
  );
  const [blkKind, setBlkKind] = useState<MentorBlackoutKind>("conference");
  const [blkLabel, setBlkLabel] = useState("");

  const submitRule = async () => {
    try {
      await m.addRule.mutateAsync({
        weekday: Number(weekday),
        start_minute: hhmmToMinutes(start),
        end_minute: hhmmToMinutes(end),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        slot_minutes: Number(slotMin),
        active: true,
      });
      toast({ title: "Rule added" });
    } catch (e) {
      toast({ title: "Could not add rule", description: String(e), variant: "destructive" });
    }
  };

  const submitWindow = async () => {
    try {
      await m.addWindow.mutateAsync({
        starts_at: new Date(winStart).toISOString(),
        ends_at: new Date(winEnd).toISOString(),
        slot_minutes: Number(slotMin),
        label: winLabel || null,
        active: true,
      });
      toast({ title: "Window added" });
      setWinLabel("");
    } catch (e) {
      toast({ title: "Could not add window", description: String(e), variant: "destructive" });
    }
  };

  const submitBlackout = async () => {
    try {
      await m.addBlackout.mutateAsync({
        starts_at: new Date(blkStart).toISOString(),
        ends_at: new Date(blkEnd).toISOString(),
        kind: blkKind,
        label: blkLabel || null,
      });
      toast({ title: "Blackout added" });
      setBlkLabel("");
    } catch (e) {
      toast({ title: "Could not add blackout", description: String(e), variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      {/* Weekly recurring */}
      <Card>
        <CardHeader>
          <CardTitle>Weekly availability</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-5">
            <div>
              <Label>Day</Label>
              <Select value={weekday} onValueChange={setWeekday}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WEEKDAYS.map((d, i) => (
                    <SelectItem key={d} value={String(i)}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Start</Label>
              <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div>
              <Label>End</Label>
              <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
            </div>
            <div>
              <Label>Slot length</Label>
              <Select value={slotMin} onValueChange={setSlotMin}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["15", "30", "45", "60", "90"].map((v) => (
                    <SelectItem key={v} value={v}>{v} min</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={submitRule} className="w-full">
                <Plus className="mr-2 h-4 w-4" /> Add
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            {rules.data?.length ? rules.data.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-md border p-3">
                <div className="text-sm">
                  <span className="font-medium">{WEEKDAYS[r.weekday]}</span>{" "}
                  {minutesToHHMM(r.start_minute)}–{minutesToHHMM(r.end_minute)}{" "}
                  · {r.slot_minutes}-min slots
                </div>
                <Button variant="ghost" size="sm" onClick={() => m.deleteRule.mutate(r.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )) : <p className="text-sm text-muted-foreground">No recurring rules yet.</p>}
          </div>
        </CardContent>
      </Card>

      {/* One-off windows */}
      <Card>
        <CardHeader>
          <CardTitle>One-off availability windows</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            <div>
              <Label>Starts</Label>
              <Input type="datetime-local" value={winStart} onChange={(e) => setWinStart(e.target.value)} />
            </div>
            <div>
              <Label>Ends</Label>
              <Input type="datetime-local" value={winEnd} onChange={(e) => setWinEnd(e.target.value)} />
            </div>
            <div>
              <Label>Label (optional)</Label>
              <Input placeholder="Pride office hours" value={winLabel} onChange={(e) => setWinLabel(e.target.value)} />
            </div>
            <div className="flex items-end">
              <Button onClick={submitWindow} className="w-full">
                <Plus className="mr-2 h-4 w-4" /> Add window
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            {windows.data?.length ? windows.data.map((w) => (
              <div key={w.id} className="flex items-center justify-between rounded-md border p-3">
                <div className="text-sm">
                  {new Date(w.starts_at).toLocaleString()} → {new Date(w.ends_at).toLocaleString()}
                  {w.label && <span className="ml-2 text-muted-foreground">· {w.label}</span>}
                </div>
                <Button variant="ghost" size="sm" onClick={() => m.deleteWindow.mutate(w.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )) : <p className="text-sm text-muted-foreground">No upcoming windows.</p>}
          </div>
        </CardContent>
      </Card>

      {/* Blackouts */}
      <Card>
        <CardHeader>
          <CardTitle>Conferences & blackout dates</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-5">
            <div>
              <Label>Starts</Label>
              <Input type="datetime-local" value={blkStart} onChange={(e) => setBlkStart(e.target.value)} />
            </div>
            <div>
              <Label>Ends</Label>
              <Input type="datetime-local" value={blkEnd} onChange={(e) => setBlkEnd(e.target.value)} />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={blkKind} onValueChange={(v) => setBlkKind(v as MentorBlackoutKind)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="conference">Conference</SelectItem>
                  <SelectItem value="event_hosting">Hosting an event</SelectItem>
                  <SelectItem value="vacation">Vacation</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Label</Label>
              <Input placeholder="SXSW 2026" value={blkLabel} onChange={(e) => setBlkLabel(e.target.value)} />
            </div>
            <div className="flex items-end">
              <Button onClick={submitBlackout} className="w-full">
                <Plus className="mr-2 h-4 w-4" /> Add
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            {blackouts.data?.length ? blackouts.data.map((b) => (
              <div key={b.id} className="flex items-center justify-between rounded-md border p-3">
                <div className="text-sm">
                  <Badge variant="secondary" className="mr-2">{b.kind}</Badge>
                  {new Date(b.starts_at).toLocaleString()} → {new Date(b.ends_at).toLocaleString()}
                  {b.label && <span className="ml-2 text-muted-foreground">· {b.label}</span>}
                </div>
                <Button variant="ghost" size="sm" onClick={() => m.deleteBlackout.mutate(b.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )) : <p className="text-sm text-muted-foreground">No upcoming blackouts.</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
